'use client';

import Icon from '@egalite/ui/Icon';
import { FC, useEffect } from 'react';

export type ApplyRequirement = {
	key: string;
	label: string;
	form: string;
};

type Props = {
	missingRequirements: ApplyRequirement[];
	onFill: (form: string) => void;
	onClose: () => void;
};

/** Agrupa as pendências pelo formulário que as resolve (um botão por formulário). */
const groupByForm = (missingRequirements: ApplyRequirement[]) =>
	missingRequirements.reduce<Array<{ form: string; labels: string[] }>>((groups, requirement) => {
		const group = groups.find(item => item.form === requirement.form);

		if (group) {
			group.labels.push(requirement.label);
			return groups;
		}

		return [...groups, { form: requirement.form, labels: [requirement.label] }];
	}, []);

/**
 * Bloqueio amigável da candidatura: lista o que falta no cadastro e abre o formulário
 * correspondente na própria página da vaga. Ver .specs/features/requisitos-candidatura.
 */
const ApplyRequirementsDialog: FC<Props> = ({ missingRequirements, onFill, onClose }) => {
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [onClose]);

	const groups = groupByForm(missingRequirements);
	const total = missingRequirements.length;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="apply-requirements-title"
				className="bg-white rounded-2xl p-8 md:p-12 max-w-3xl w-full mx-4 shadow-xl"
				onClick={event => event.stopPropagation()}
			>
				<h3 id="apply-requirements-title" className="text-3xl font-bold text-gray-900 mb-4">
					Complete seu cadastro para se candidatar
				</h3>
				<p className="text-gray-600 text-xl leading-relaxed mb-8">
					{total === 1
						? 'Falta 1 informação obrigatória. Você pode preencher aqui mesmo e a candidatura é enviada em seguida.'
						: `Faltam ${total} informações obrigatórias. Você pode preencher aqui mesmo e a candidatura é enviada em seguida.`}
				</p>

				<ul className="flex flex-col gap-4 mb-8">
					{groups.map(({ form, labels }) => (
						<li
							key={form}
							className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-gray-200 rounded-xl p-4"
						>
							<span className="flex items-center gap-3 text-xl text-gray-700">
								<Icon name="circle-dashed" className="text-brand-main" aria-hidden="true" />
								{labels.join(', ')}
							</span>
							<button
								type="button"
								onClick={() => onFill(form)}
								className="px-8 py-3 bg-brand-main text-white rounded-full text-lg font-medium hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
							>
								{labels.length > 1 ? 'Preencher' : `Preencher ${labels[0].toLowerCase()}`}
							</button>
						</li>
					))}
				</ul>

				<button
					type="button"
					onClick={onClose}
					className="w-full px-6 py-4 border border-gray-300 rounded-full text-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
				>
					Agora não
				</button>
			</div>
		</div>
	);
};

export default ApplyRequirementsDialog;
